import { useEffect, useRef, useState } from 'react';
import { Alert, Box, Button, IconButton, Typography } from '@mui/material';
import { ChevronLeft, ChevronRight, ZoomIn, ZoomOut } from '@mui/icons-material';
import type { PDFDocumentLoadingTask, PDFDocumentProxy, PDFPageProxy } from 'pdfjs-dist';
import { loadFacilityPdf } from './loadFacilityPdf';

const ZOOM_LEVELS = [1, 1.5, 2, 3, 4, 6];
const WIDE_VIEWER_WIDTH = 768;
const MAX_CANVAS_PIXELS = 8_000_000;
const controlSize = { width: 44, height: 44 };

export default function FacilityPdfViewer({ pdfUrl, label }: { pdfUrl: string; label: string }) {
  const scrollArea = useRef<HTMLDivElement>(null);
  const canvas = useRef<HTMLCanvasElement>(null);
  const drag = useRef<{ x: number; y: number; left: number; top: number } | null>(null);
  const [size, setSize] = useState({ width: 0, height: 0, isWide: false });
  const [pdf, setPdf] = useState<PDFDocumentProxy>();
  const [page, setPage] = useState<PDFPageProxy>();
  const [pageNumber, setPageNumber] = useState(1);
  const [zoom, setZoom] = useState(0);
  const [preferWholePage, setPreferWholePage] = useState(false);
  const [ready, setReady] = useState(false);
  const [error, setError] = useState('');
  const fitsWholePage = !size.isWide || preferWholePage;
  const showFitWidth = size.isWide && fitsWholePage && zoom === 0;

  useEffect(() => {
    const area = scrollArea.current!;
    const measure = () => setSize({
      width: area.clientWidth,
      height: area.clientHeight,
      isWide: area.offsetWidth >= WIDE_VIEWER_WIDTH,
    });
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(area);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    let cancelled = false;
    let loadingTask: PDFDocumentLoadingTask | undefined;

    loadFacilityPdf().then(async getDocument => {
      if (cancelled) return;
      loadingTask = getDocument({ url: pdfUrl, isEvalSupported: false });
      const document = await loadingTask.promise;
      if (!cancelled) setPdf(document);
    }).catch(() => {
      if (!cancelled) setError('The map could not be loaded. Open it in a new tab or download the PDF.');
    });

    return () => {
      cancelled = true;
      void loadingTask?.destroy().catch(() => undefined);
    };
  }, [pdfUrl]);

  useEffect(() => {
    if (!pdf) return;
    let cancelled = false;
    setError('');
    pdf.getPage(pageNumber).then(nextPage => {
      if (!cancelled) setPage(nextPage);
    }).catch(() => {
      if (!cancelled) setError('This map page could not be loaded. Try another page or open the PDF.');
    });
    return () => { cancelled = true; };
  }, [pdf, pageNumber]);

  useEffect(() => {
    if (page?.pageNumber !== pageNumber || !size.width || !size.height) return;
    const surface = canvas.current!;
    const context = surface.getContext('2d');
    const original = page.getViewport({ scale: 1 });
    const widthScale = size.width / original.width;
    const fitScale = fitsWholePage ? Math.min(widthScale, size.height / original.height) : widthScale;
    const viewport = page.getViewport({ scale: fitScale * ZOOM_LEVELS[zoom] });
    const pixelRatio = Math.min(
      window.devicePixelRatio || 1,
      Math.sqrt(MAX_CANVAS_PIXELS / (viewport.width * viewport.height)),
    );
    if (!context) {
      setError('This map page could not be displayed. Open it in a new tab or download the PDF.');
      return;
    }
    surface.width = Math.ceil(viewport.width * pixelRatio);
    surface.height = Math.ceil(viewport.height * pixelRatio);
    surface.style.width = `${Math.floor(viewport.width)}px`;
    surface.style.height = `${Math.floor(viewport.height)}px`;
    let cancelled = false;
    setError('');
    const renderTask = page.render({ canvasContext: context, viewport, transform: [pixelRatio, 0, 0, pixelRatio, 0, 0] });
    renderTask.promise.then(() => {
      if (!cancelled) setReady(true);
    }).catch(() => {
      if (!cancelled) {
        setReady(false);
        setError('This map page could not be displayed. Open it in a new tab or download the PDF.');
      }
    });
    return () => {
      cancelled = true;
      renderTask.cancel();
    };
  }, [page, pageNumber, size, zoom, fitsWholePage]);

  const changeFit = () => {
    if (size.isWide) setPreferWholePage(!showFitWidth);
    setZoom(0);
    scrollArea.current?.scrollTo(0, 0);
  };

  const changePage = (number: number) => {
    setReady(false);
    setPageNumber(number);
    scrollArea.current?.scrollTo(0, 0);
  };

  return (
    <Box sx={{ minWidth: 0, border: 1, borderColor: 'divider', borderRadius: 1 }}>
      <Box sx={{
        display: 'grid',
        gridTemplateColumns: { xs: 'minmax(0, 1fr)', sm: 'minmax(0, 1fr) auto minmax(0, 1fr)' },
        alignItems: 'center',
        justifyItems: 'center',
        px: 0.5,
      }}>
        <Box role="group" aria-label="Map pages" sx={{ display: 'flex', alignItems: 'center', gridColumn: { xs: 1, sm: 2 } }}>
          <IconButton aria-label="Previous page" sx={controlSize} disabled={pageNumber <= 1} onClick={() => changePage(pageNumber - 1)}>
            <ChevronLeft />
          </IconButton>
          <Typography
            aria-live="polite"
            sx={{
              px: 1.5, py: 0.5, borderRadius: 1,
              bgcolor: '#FFD66B', color: '#681D25',
              fontSize: '1rem', fontWeight: 800, fontVariantNumeric: 'tabular-nums',
            }}
          >
            {pdf ? `Page ${pageNumber} of ${pdf.numPages}` : 'Map pages'}
          </Typography>
          <IconButton aria-label="Next page" sx={controlSize} disabled={!pdf || pageNumber >= pdf.numPages} onClick={() => changePage(pageNumber + 1)}>
            <ChevronRight />
          </IconButton>
        </Box>
        <Box role="group" aria-label="Map zoom" sx={{
          display: 'flex', alignItems: 'center', gridColumn: { xs: 1, sm: 3 }, justifySelf: { xs: 'center', sm: 'end' },
        }}>
          <IconButton aria-label="Zoom out" sx={controlSize} disabled={!ready || zoom === 0} onClick={() => setZoom(zoom - 1)}>
            <ZoomOut />
          </IconButton>
          <Button size="small" sx={{ minHeight: 44 }} disabled={!ready || (!size.isWide && zoom === 0)} onClick={changeFit}>
            {showFitWidth ? 'Fit width' : 'Fit page'}
          </Button>
          <IconButton aria-label="Zoom in" sx={controlSize} disabled={!ready || zoom === ZOOM_LEVELS.length - 1} onClick={() => setZoom(zoom + 1)}>
            <ZoomIn />
          </IconButton>
        </Box>
      </Box>
      {error && <Alert severity="error">{error}</Alert>}
      <Box
        ref={scrollArea}
        role="region"
        aria-label={`${label} PDF`}
        tabIndex={0}
        onPointerDown={event => {
          if (event.pointerType !== 'mouse' || event.button !== 0 || !ready || event.target !== canvas.current) return;
          const area = event.currentTarget;
          if (area.scrollWidth <= area.clientWidth && area.scrollHeight <= area.clientHeight) return;
          area.setPointerCapture(event.pointerId);
          drag.current = { x: event.clientX, y: event.clientY, left: area.scrollLeft, top: area.scrollTop };
        }}
        onPointerMove={event => {
          if (!drag.current) return;
          event.currentTarget.scrollLeft = drag.current.left + drag.current.x - event.clientX;
          event.currentTarget.scrollTop = drag.current.top + drag.current.y - event.clientY;
        }}
        onLostPointerCapture={() => { drag.current = null; }}
        sx={{
          position: 'relative', overflow: 'auto', bgcolor: 'action.hover',
          height: 'clamp(200px, 55vh, 660px)',
          '@supports (height: 1svh)': { height: 'clamp(200px, 55svh, 660px)' },
          '& canvas': { flex: 'none', m: 'auto', display: 'block', userSelect: 'none', cursor: zoom || !fitsWholePage ? 'grab' : 'auto' },
        }}
      >
        {!ready && !error && <Typography role="status" sx={{ position: 'absolute', p: 2 }}>Loading map page…</Typography>}
        <Box sx={{ display: 'flex', width: 'max-content', minWidth: '100%', minHeight: '100%' }}>
          <canvas ref={canvas} role="img" aria-label={`${label}, page ${pageNumber}`} style={{ visibility: ready ? 'visible' : 'hidden' }} />
        </Box>
      </Box>
    </Box>
  );
}
